import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { ProcessorDetails } from '@/lib/api/processors'

interface NMI3DSCardProps {
    processor: ProcessorDetails
}

const get3DSConfig = (options: Record<string, unknown> | null) => {
    return {
        merchantName: options?.merchantName as string | undefined,
        merchantUrl: options?.merchantUrl as string | undefined,
        merchantCountryCode: options?.merchantCountryCode as string | undefined,
        amexRequestorType: options?.amexRequestorType as string | undefined,
        cbSiretNumber: options?.cbSiretNumber as string | undefined,
        discoverClientId: options?.discoverClientId as string | undefined,
        acquirerBinVisa: options?.acquirerBinVisa as string | undefined,
        acquirerBinMastercard: options?.acquirerBinMastercard as string | undefined,
        acquirerBinAmex: options?.acquirerBinAmex as string | undefined,
        acquirerBinDiscover: options?.acquirerBinDiscover as string | undefined,
        acquirerBinJcb: options?.acquirerBinJcb as string | undefined,
        acquirerBinDinersClub: options?.acquirerBinDinersClub as string | undefined,
        acquirerBinCB: options?.acquirerBinCB as string | undefined,
    }
}

const getEnable3DS = (options: Record<string, unknown> | null) => {
    return options?.enable3DS === true
}

export const NMI3DSCard = ({ processor }: NMI3DSCardProps) => {
    const { t } = useTranslation('processors')
    const enable3DS = getEnable3DS(processor.options)
    const config = get3DSConfig(processor.options)

    if (!enable3DS) {
        return null
    }

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>{t('universal3DSConfiguration')}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <p className="text-sm text-muted-foreground">{t('merchantName')}</p>
                            <p className="font-semibold">{config.merchantName || t('notConfigured')}</p>
                        </div>

                        <div>
                            <p className="text-sm text-muted-foreground">{t('merchantUrl')}</p>
                            <p className="font-semibold">{config.merchantUrl || t('notConfigured')}</p>
                        </div>

                        <div>
                            <p className="text-sm text-muted-foreground">{t('merchantCountryCode')}</p>
                            <p className="font-semibold">{config.merchantCountryCode || t('notConfigured')}</p>
                        </div>
                    </div>

                    <div>
                        <p className="text-sm font-medium text-muted-foreground mb-3">{t('networkSpecificInfo')}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <p className="text-sm text-muted-foreground">{t('amexRequestorType')}</p>
                                <p className="font-semibold">{config.amexRequestorType || t('notConfigured')}</p>
                            </div>

                            <div>
                                <p className="text-sm text-muted-foreground">{t('cbSiretNumber')}</p>
                                <p className="font-semibold">{config.cbSiretNumber || t('notConfigured')}</p>
                            </div>

                            <div>
                                <p className="text-sm text-muted-foreground">{t('discoverClientId')}</p>
                                <p className="font-semibold">{config.discoverClientId || t('notConfigured')}</p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <p className="text-sm font-medium text-muted-foreground mb-3">{t('acquirerBINs')}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <p className="text-sm text-muted-foreground">{t('visaAcquirerBin')}</p>
                                <p className="font-mono text-sm">{config.acquirerBinVisa || t('notConfigured')}</p>
                            </div>

                            <div>
                                <p className="text-sm text-muted-foreground">{t('mastercardAcquirerBin')}</p>
                                <p className="font-mono text-sm">{config.acquirerBinMastercard || t('notConfigured')}</p>
                            </div>

                            <div>
                                <p className="text-sm text-muted-foreground">{t('amexAcquirerBin')}</p>
                                <p className="font-mono text-sm">{config.acquirerBinAmex || t('notConfigured')}</p>
                            </div>

                            <div>
                                <p className="text-sm text-muted-foreground">{t('discoverAcquirerBin')}</p>
                                <p className="font-mono text-sm">{config.acquirerBinDiscover || t('notConfigured')}</p>
                            </div>

                            <div>
                                <p className="text-sm text-muted-foreground">{t('jcbAcquirerBin')}</p>
                                <p className="font-mono text-sm">{config.acquirerBinJcb || t('notConfigured')}</p>
                            </div>

                            <div>
                                <p className="text-sm text-muted-foreground">{t('dinersClubAcquirerBin')}</p>
                                <p className="font-mono text-sm">{config.acquirerBinDinersClub || t('notConfigured')}</p>
                            </div>

                            <div>
                                <p className="text-sm text-muted-foreground">{t('cbAcquirerBin')}</p>
                                <p className="font-mono text-sm">{config.acquirerBinCB || t('notConfigured')}</p>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-lg bg-blue-50 p-3 border border-blue-200">
                        <p className="text-sm text-blue-700">
                            <strong>{t('note')}:</strong> {t('descriptorUsedFor3DS')}
                        </p>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

